// Purpose: Encapsulates the Monday SDK and provides a typed interface to the Monday API.
import mondaySdk from "monday-sdk-js";

const gql = String.raw; // This just enables syntax highlighting for gql strings in some IDEs
const monday = mondaySdk();
/*
  Current monday.com docs (https://developer.monday.com/api-reference/docs/api-versioning) say:

  We strongly encourage production applications to pass a version name in each API call. If you don't, your app will
  always get the Current version. Passing a version name makes your app less susceptible to breaking changes and gives
  you more time to migrate to a new version.

  This part about the current version is not true. Currently, when no API version is specified in a request, the
  corresponding response includes this header: "Api-Version: 2023-07". The above docs list 2024-01 as the current
  version.

  The cake is a lie.
 */
monday.setApiVersion("2024-01");

export type MondayServiceType = {
  fetchSessionToken: () => Promise<string>;
  fetchBoardContext: () => Promise<MondayBoardContext>;
  fetchMondayPlan: () => Promise<MondayPlan>;
  fetchAccountSlug: () => Promise<string>;
  accountSlug: string;
  listenToBoardContext: (callback: (context: MondayBoardContext) => void) => void;
};

export class MondayService implements MondayServiceType {
  #sessionToken?: string;
  #boardContext?: MondayBoardContext;
  #plan?: MondayPlan;
  #accountSlug = "";

  async fetchSessionToken() {
    if (this.#sessionToken) {
      return this.#sessionToken;
    }
    const response = (await monday.get("sessionToken")) as MondayGetResponse<string>;
    this.#sessionToken = response.data;
    return this.#sessionToken;
  }

  async fetchBoardContext() {
    if (this.#boardContext) {
      return this.#boardContext;
    }
    const response = (await monday.get("context")) as MondayGetResponse<MondayBoardContext>;
    this.#boardContext = response.data;
    return this.#boardContext;
  }

  async fetchMondayPlan() {
    if (this.#plan) {
      return this.#plan;
    }
    const result = (await monday.api(gql`
      query {
        account {
          plan {
            max_users
            period
            tier
            version
          }
        }
      }
    `)) as MondayGetResponse<MondayPlanResponse>;
    this.#plan = result.data.account.plan;
    return this.#plan;
  }

  // TODO: Consolidate this w/ the query above to return both the plan and the slug
  async fetchAccountSlug(): Promise<string> {
    if (this.#accountSlug) {
      return this.#accountSlug;
    }
    const result = (await monday.api(gql`
      query {
        me {
          account {
            slug
          }
        }
      }
    `)) as MondayGetResponse<MondaySlugResponse>;
    this.#accountSlug = result.data.me.account.slug;
    return this.#accountSlug;
  }

  get accountSlug() {
    return this.#accountSlug;
  }

  listenToBoardContext(callback: (data: MondayBoardContext) => void) {
    // This is a workaround for the monday.listen method not having an unsubscribe counterpart; it doesn't really
    // unsubscribe, but it will stop calling the callback when the component is unmounted.
    let unsubscribed = false;
    monday.listen("context", (response: { data: MondayBoardContext }) => {
      if (!unsubscribed) {
        callback(response.data);
      }
    });
    return () => {
      unsubscribed = true;
    };
  }
}

export interface MondayGetResponse<T> {
  data: T;
}

export interface MondayUser {
  id: string;
  isViewOnly: boolean;
  isGuest: boolean;
  isAdmin: boolean;
}

export interface MondaySubscription {
  plan_id: string;
  renewal_date: string;
  is_trial: boolean;
  billing_period: string;
  days_left: number;
}

export interface MondayBoardContext {
  boardId: number;
  boardIds?: number[];
  boardViewId?: number;
  workspaceId: number;
  theme: Theme;
  user?: MondayUser;
  account: {
    id: string;
  };
  app: {
    id: string;
    clientId: string;
  };
  subscription?: MondaySubscription;
  appVersion: {
    versionData: VersionData;
  };
}

export enum Theme {
  LIGHT = "light",
  DARK = "dark",
  BLACK = "black",
  HACKER = "hacker_theme",
}

export const mondayThemes = [Theme.LIGHT, Theme.DARK, Theme.BLACK, Theme.HACKER];

export interface VersionData {
  major: number;
  minor: number;
  patch: number;
}

export interface MondayPlan {
  max_users: number;
  period: string;
  tier: string;
  version: number;
}

interface MondayPlanResponse {
  account: {
    plan: MondayPlan;
  };
}

interface MondaySlugResponse {
  me: {
    account: {
      slug: string;
    };
  };
}
